import {
  SadDoctor,
  Refresh,
  Graph,
  type SVGIconProps,
  Headphone,
  Bell,
  InformationCircle,
} from "@repo/icons";
import { Link, useRouter, useSearch } from "@tanstack/react-router";
import { CpsButtonText } from "corpus";
import { type FunctionComponent } from "react/jsx-runtime";
import {
  EmptyStateRoot,
  EmptyStateImage,
  EmptyStateBody,
} from "@/components/empty-state";
import { Button } from "@/components/button";
import {
  IconListItem,
  IconListItemIcon,
  IconListItemText,
  IconListRoot,
} from "@/components/icon-list";
import {
  MessageDrawerActionButton,
  MessageDrawerActions,
  MessageDrawerBody,
  MessageDrawerRoot,
  MessageDrawerTitle,
} from "@/components/message-drawer";

interface ListItemData {
  Icon: FunctionComponent<SVGIconProps>;
  content: JSX.Element;
}

export const UnsubscribeAssistProComponent = (): JSX.Element => {
  const listItems: ListItemData[] = [
    {
      Icon: Headphone,
      content: (
        <>
          <p className="font-bold">Atendimento personalizado ao paciente</p>
          <p>Fim do suporte do time de atendimento.</p>
        </>
      ),
    },
    {
      Icon: Refresh,
      content: (
        <>
          <p className="font-bold">Recuperação de agendamentos</p>
          <p>Interrupção dos contatos automáticos para recuperar agendamentos.</p>
        </>
      ),
    },
    {
      Icon: Graph,
      content: (
        <>
          <p className="font-bold">Relatório de contatos</p>
          <p>Perda da visão da procura e conversões do consultório.</p>
        </>
      ),
    },
    {
      Icon: Bell,
      content: (
        <>
          <p className="font-bold">Lembretes de retorno</p>
          <p>Fim das automações para lembrar pacientes de novos atendimentos.</p>
        </>
      ),
    },
  ];
  return (
    <>
      <EmptyStateRoot>
        <p className="text-2xl font-semibold text-left">
          Cancelar reduz recursos de atendimento e gestão
        </p>
        <EmptyStateImage
          icon={SadDoctor}
          alt="Imagem de um médico triste"
          className="w-full h-[25vh]"
        />
        <EmptyStateBody>
          <IconListRoot className="flex flex-col gap-2 px-6 py-4 text-sm font-normal text-neutral-500">
            {listItems.map((item) => (
              <IconListItem className="flex gap-2" key={item.Icon.name}>
                <IconListItemIcon className="self-center fill-primary-400 shrink-0">
                  <item.Icon size={24} />
                </IconListItemIcon>
                <IconListItemText className="self-end">{item.content}</IconListItemText>
              </IconListItem>
            ))}
          </IconListRoot>
        </EmptyStateBody>
        <div className="flex flex-col gap-2 w-full">
          <Link
            to="/settings/assist/unsubscribe-assist-pro"
            search={{ confirmUnsubscribe: true }}
            className="w-full"
          >
            <Button className="font-medium" fullWidth>
              Confirmar cancelamento
            </Button>
          </Link>
          <Link to="/settings/assist" className="w-full">
            <div className="flex justify-center pb-2">
              <CpsButtonText>Continuar com minha assinatura</CpsButtonText>
            </div>
          </Link>
        </div>
      </EmptyStateRoot>
      <ConfirmUnsubscribeDrawer />
    </>
  );
};

const ConfirmUnsubscribeDrawer = (): JSX.Element => {
  const searchParams = useSearch({
    from: "/settings/assist/unsubscribe-assist-pro/",
  });
  const router = useRouter();

  return (
    <MessageDrawerRoot
      icon={InformationCircle}
      setOpen={(open) => {
        if (!open) {
          void router.navigate({
            search: {
              confirmUnsubscribe: false,
            },
            replace: true,
          });
        }
      }}
      open={Boolean(searchParams.confirmUnsubscribe)}
      variant="secondary"
    >
      <MessageDrawerTitle>
        Seu telefone Livance seguirá ativo por 3 meses
      </MessageDrawerTitle>
      <MessageDrawerBody>
          <p className="pt-4">
            Durante esse período, pacientes que entrarem em contato serão informados sobre
            seu novo telefone.
          </p>
      </MessageDrawerBody>
      <MessageDrawerActions>
        <MessageDrawerActionButton
          onClick={() =>
            void router.navigate({
              to: "/settings/assist/unsubscribe-assist-pro/upsert-phone/",
            })
          }
        >
          Continuar com o cancelamento
        </MessageDrawerActionButton>
        <MessageDrawerActionButton
          secondary
          onClick={() => void router.navigate({ to: "/settings/assist/" })}
        >
          Manter telefone e assinatura
        </MessageDrawerActionButton>
      </MessageDrawerActions>
    </MessageDrawerRoot>
  );
};
